import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { NewBuildNotice, usePresenceHeartbeat } from '@iqd/shared-app';
import { useHostAuth } from '../shared/ui';
import { environment } from '../../environments/environment';
import { MenuBar } from './menu-bar';

/** How a page tells the shell it will do its own scrolling. See below. */
const ViewportFill = createContext<(fill: boolean) => void>(() => undefined);

/**
 * Hand this page exactly the room left between the menu bar and the footer,
 * and let it scroll whichever part of itself it chooses.
 *
 * The window scrolls by default, which is right for a page that is a column
 * of content. It is wrong for a page built as panes — the project view, whose
 * picker and header belong on screen while a tab is read — and measuring the
 * chrome in `100vh` arithmetic only holds until the menu bar wraps or the test
 * environment banner appears above it. The shell knows its own height, so it
 * is the shell that gives the room away.
 */
export function useViewportFill() {
  const setFill = useContext(ViewportFill);
  useEffect(() => {
    setFill(true);
    return () => setFill(false);
  }, [setFill]);
}

/**
 * The chrome around every page: the menu bar above, the footer below.
 *
 * `?popout=1` takes both off. That is a window opened on its own — today the
 * detailers diary dragged onto a second monitor — which wants the whole screen
 * for the page.
 */
export function AppShell({
  children,
  onPrefetchRoute,
}: {
  children: ReactNode;
  /**
   * Handed down to the menu, which calls it when someone points at an item.
   * Passed rather than imported: the map it reads is app.tsx's, and importing
   * it here would close a loop from app.tsx through this file and back.
   */
  onPrefetchRoute?: (path: string) => void;
}) {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useHostAuth();
  const [fill, setFill] = useState(false);

  // Here rather than on any one screen: the shell is the only thing that is
  // mounted on all of them, including a popped-out window with no chrome.
  usePresenceHeartbeat('iqd-pro');

  const chrome = searchParams.get('popout') !== '1';
  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <div
      className={`flex flex-col bg-white text-hilevel-slate ${fill ? 'h-screen' : 'min-h-screen'}`}
    >
      {chrome && <MenuBar onPrefetchRoute={onPrefetchRoute} />}

      {/* Above the test-environment banner and below the menu: a deploy that
          has landed under this tab is news about the whole page, not about
          whichever screen is open. Inside `chrome` because a popped-out window
          is one screen on a second monitor, and pulling it back to reload is
          the owner tab's job. */}
      {chrome && <NewBuildNotice />}

      {environment.testEnvironment && isAuthenticated && chrome && (
        <p className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-hilevel-slate">
          This is the iQD <strong>test environment</strong>. Nothing you do here reaches the real
          business, and email is redirected to{' '}
          <a href="https://mailpit.staging.iqd-pro.com/" target="_blank" rel="noopener noreferrer">
            Mailpit
          </a>
          . The data here is overwritten regularly.
        </p>
      )}

      {/* `min-h-0` so a filling page is held to the room that is left rather
          than pushing the footer off the bottom of the screen. */}
      <main className={`flex-1 ${fill ? 'min-h-0' : ''}`}>
        <ViewportFill.Provider value={setFill}>{children}</ViewportFill.Provider>
      </main>

      {chrome && (
        <footer className="border-t border-hilevel-border px-4 py-3 text-center">
          <small className="text-hilevel-gray">
            © 2016 – {year}, The Mezzanine Floor Group Ltd. All rights reserved.{' '}
            <Link to="/terms-of-use">Terms of Use</Link>
            {isAuthenticated && (
              <>
                {' · '}
                <Link to="/changelog">What's New</Link>
              </>
            )}
          </small>
        </footer>
      )}
    </div>
  );
}
